import { useTranslation } from "@ferrivia/i18n";
import { AnswerEntry } from "./AnswerEntry";
import type { AnswerMap } from "./answerMap";
import { articlesFor } from "./articles";
import { Disclosure } from "./Disclosure";
import { SlotArticle } from "./SlotArticle";
import { visibleSections } from "./partVisibility";
import { entryHasContent, type Section } from "./summaryLayout";
import s from "./TaustatiedotPart.module.css";

function useSectionTitles(): Record<Section["id"], string> {
  const { t } = useTranslation("client");

  return {
    lifeStage: t("summary.sections.lifeStage", "Elämänvaihe"),
    conditions: t("summary.sections.conditions", "Sairaudet ja lääkitys"),
    menstruation: t("summary.sections.menstruation", "Kuukautiset"),
    pregnancy: t("summary.sections.pregnancy", "Raskaudet ja ehkäisy"),
    bloodDonation: t("summary.sections.bloodDonation", "Verenluovutus"),
    diet: t("summary.sections.diet", "Ruokavalio"),
    exercise: t("summary.sections.exercise", "Liikunta"),
    gut: t("summary.sections.gut", "Suolisto"),
    other: t("summary.sections.other", "Muut tiedot"),
  };
}

export function TaustatiedotPart({ answers }: { answers: AnswerMap }) {
  const { t } = useTranslation("client");
  const titles = useSectionTitles();

  return (
    <div className={s.root}>
      {visibleSections(answers).map((section) => {
        const folded = section.folded;
        const hasFolded = folded?.entries.some((entry) => entryHasContent(entry, answers)) ?? false;

        return (
          <section key={section.id} className={s.section} aria-labelledby={`summary-section-${section.id}`}>
            <h3 id={`summary-section-${section.id}`} className={s.sectionTitle}>
              {titles[section.id]}
            </h3>
            {section.lead.some((entry) => entryHasContent(entry, answers)) && (
              <div className={s.entries}>
                {section.lead.map((entry, index) => (
                  <AnswerEntry key={index} entry={entry} answers={answers} />
                ))}
              </div>
            )}
            {section.article && articlesFor(section.article, answers).map((slot) => <SlotArticle key={slot.id} slot={slot} />)}
            {folded && hasFolded && (
              <Disclosure
                sectionId={folded.disclosure}
                foldedLabel={
                  folded.foldedLabel === "others"
                    ? t("summary.showOtherAnswers", "Näytä muut vastauksesi")
                    : t("summary.showAnswers", "Näytä vastauksesi")
                }
                openLabel={t("summary.hideAnswers", "Piilota vastaukset")}
              >
                <div className={s.entries}>
                  {folded.entries.map((entry, index) => (
                    <AnswerEntry key={index} entry={entry} answers={answers} />
                  ))}
                </div>
              </Disclosure>
            )}
          </section>
        );
      })}
    </div>
  );
}
